/**
 * Workspace Page - Modern panel-based workspace
 * Replaces traditional app switching with unlimited panels
 */

import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import Layout from '../components/project/Layout'
import PanelWorkspace from '../components/workspace/PanelWorkspace'
import WorkspaceHeaderConsolidated from '../components/workspace/WorkspaceHeaderConsolidated'
import SimpleEnhancedHeader from '../components/workspace/SimpleEnhancedHeader'
import { trpc } from '../lib/trpc'
import { Loader2, Focus } from 'lucide-react'
import { useAuthStore } from '../stores/auth-store'
import { useUIStore } from '../stores/ui-store'
import CompactTaskModal from '../components/kanban/CompactTaskModal'
import TaskDetail from '../components/task/TaskDetail'
import LayoutManagerModal from '../components/workspace/LayoutManagerModal'

export default function Workspace() {
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  
  // State for viewing task from command palette
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(null)
  const [isTaskViewModalOpen, setIsTaskViewModalOpen] = React.useState(false)
  
  // All hooks must be called unconditionally
  const utils = trpc.useUtils()
  const { 
    showTaskCreationModal, 
    taskCreationColumnId,
    taskCreationKanbanId,
    openTaskCreationModal, 
    closeTaskCreationModal,
    showLayoutManager,
    layoutManagerMode,
    openLayoutManager,
    closeLayoutManager,
    triggerNoteCreation,
    toggleFocusMode,
    focusMode
  } = useUIStore()
  
  const { data: workspaces, isLoading } = trpc.workspace.list.useQuery(undefined, {
    staleTime: 300000, // Cache for 5 minutes
    cacheTime: 600000, // Keep in cache for 10 minutes
  })

  // On first load (no :workspaceId), redirect to last viewed workspace if available
  React.useEffect(() => {
    if (!isLoading && workspaces && workspaces.length > 0 && !workspaceId) {
      const lastId = localStorage.getItem('lastWorkspaceId')
      const exists = lastId && workspaces.some(w => w.id === lastId)
      const targetId = exists ? (lastId as string) : workspaces[0].id
      if (targetId) {
        navigate(`/workspace/${targetId}`, { replace: true })
      }
    }
  }, [isLoading, workspaces, workspaceId, navigate])
  
  // Use workspace from URL params, or fallback to first workspace
  const activeWorkspace = workspaceId 
    ? workspaces?.find(w => w.id === workspaceId)
    : workspaces?.find(w => w._count.projects > 0 || w._count.teams > 0 || w._count.kanbans > 0) || workspaces?.[0]

  // Persist last viewed workspace id
  React.useEffect(() => {
    if (activeWorkspace?.id) {
      localStorage.setItem('lastWorkspaceId', activeWorkspace.id)
    }
  }, [activeWorkspace?.id])

  const { data: panels = [] } = trpc.panel.list.useQuery(
    { workspaceId: activeWorkspace?.id ?? '' },
    { 
      enabled: !!activeWorkspace?.id,
      staleTime: 60000, // Cache for 1 minute
      cacheTime: 300000, // Keep in cache for 5 minutes
    }
  )
  
  // Get the first kanban board to create tasks in
  const { data: kanbans = [] } = trpc.kanban.list.useQuery(
    { workspaceId: activeWorkspace?.id ?? '' },
    { 
      enabled: !!activeWorkspace?.id,
      staleTime: 60000,
      cacheTime: 300000,
    }
  )
  
  const firstKanban = kanbans[0]
  
  // Get kanban columns for the first kanban
  const { data: firstKanbanData } = trpc.kanban.get.useQuery(
    { id: firstKanban?.id ?? '' },
    { 
      enabled: !!firstKanban?.id,
      staleTime: 60000,
      cacheTime: 300000,
    }
  )
  
  const createPanel = trpc.panel.create.useMutation({
    onSuccess: () => {
      // Refetch panels after creation
      if (activeWorkspace) {
        utils.panel.list.invalidate({ workspaceId: activeWorkspace.id })
      }
    }
  })

  const toggleMinimizeAll = trpc.panel.toggleMinimizeAll.useMutation({
    onSuccess: () => {
      if (activeWorkspace) {
        utils.panel.list.invalidate({ workspaceId: activeWorkspace.id })
      }
    }
  })

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  // Keyboard shortcut handler for focus mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+Shift+F or Ctrl+Shift+F
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault()
        toggleFocusMode()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-krushr-primary" />
            <p className="text-gray-600">Loading workspace...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!activeWorkspace) {
    return (
      <Layout>
        <div className="flex flex-col min-h-full">
          <div className="flex items-center justify-center flex-1">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Workspace Found</h2>
              <p className="text-gray-600">
                You need to create or join a workspace to use the panel system.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const handleCreatePanel = (type: string) => {
    if (!activeWorkspace) return
    
    // Special handling for task creation - open modal instead of creating a panel
    if (type === 'TASK') {
      // Get the first kanban column if available
      const firstKanbanColumn = firstKanbanData?.columns?.[0]
      openTaskCreationModal({ 
        kanbanId: firstKanban?.id,
        columnId: firstKanbanColumn?.id 
      })
      return
    }
    
    // Special handling for note creation - trigger note creation in existing panel
    if (type === 'NOTE') {
      triggerNoteCreation()
      return
    }
    
    // Create a new panel based on type
    const panelConfig = {
      KANBAN: { title: 'New Kanban Board', width: 8, height: 12 },
      NOTES: { title: 'New Note', width: 6, height: 10 },
      CALENDAR: { title: 'Calendar', width: 10, height: 14 },
      CHAT: { title: 'Chat', width: 6, height: 12 },
      AI_CHAT: { title: 'AI Assistant', width: 6, height: 12 },
      DASHBOARD: { title: 'Dashboard', width: 8, height: 10 },
    }
    
    const config = panelConfig[type as keyof typeof panelConfig] || { title: 'New Panel', width: 6, height: 10 }
    
    createPanel.mutate({
      workspaceId: activeWorkspace.id,
      type,
      title: config.title,
      position: { x: 0, y: 0 },
      size: { width: config.width, height: config.height },
      data: {}
    })
  }

  const handleAction = (action: string) => {
    console.log('handleAction called with:', action)
    // Handle open-task action for viewing tasks from command palette
    if (action.startsWith('open-task:')) {
      const taskId = action.replace('open-task:', '')
      console.log('Opening task for task ID:', taskId)
      
      // Find the task element in the kanban board and scroll to it
      setTimeout(() => {
        const taskElement = document.querySelector(`[data-task-id="${taskId}"]`)
        if (taskElement) {
          // Scroll the task into view
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          
          // Add highlight effect
          taskElement.classList.add('ring-2', 'ring-krushr-primary', 'ring-offset-2')
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            taskElement.classList.remove('ring-2', 'ring-krushr-primary', 'ring-offset-2')
          }, 3000)
          
          // Click the task to open its details (if it has a click handler)
          const clickEvent = new MouseEvent('click', { bubbles: true })
          taskElement.dispatchEvent(clickEvent)
        } else {
          // If task not visible, try opening the modal as fallback
          setSelectedTaskId(taskId)
          setIsTaskViewModalOpen(true)
        }
      }, 200)
      
      return
    }
    
    switch (action) {
      case 'logout':
        const authStore = useAuthStore.getState()
        authStore.logout()
        navigate('/login')
        break
        
      case 'save-layout':
        openLayoutManager('save')
        break
        
      case 'load-layout':
        openLayoutManager('load')
        break
        
      case 'focus-mode':
        toggleFocusMode()
        break
        
      case 'minimize-all':
        handleMinimizeAll()
        break
        
      case 'view-grid':
      case 'view-list':
        // TODO: Implement view switching
        break
        
      case 'about':
        // Show about modal/dialog with app information
        alert(`Krushr v1.0.0\n\nProject management workspace with AI-powered assistance.\n\nFeatures:\n• Kanban boards\n• AI chat assistant\n• Team collaboration\n• Real-time updates\n\nBuilt with React, TypeScript, and tRPC`)
        break
        
      case 'ai-analyze':
      case 'ai-schedule':
      case 'ai-suggest':
        // TODO: Implement AI features
        break
        
      case 'ai-assistant':
        // Create AI Assistant panel
        handleCreatePanel('AI_CHAT')
        break
        
      default:
    }
  }

  const handleMinimizeAll = () => {
    if (!activeWorkspace) return
    
    // Check if any panels are currently not minimized
    const hasNonMinimizedPanels = (panels || []).some(panel => !panel.is_minimized)
    
    // If all panels are already minimized, restore them; otherwise minimize all
    toggleMinimizeAll.mutate({
      workspaceId: activeWorkspace.id,
      minimize: hasNonMinimizedPanels
    })
  }

  return (
    <Layout>
      <div className="flex flex-col min-h-full w-full bg-transparent">
        {/* Focus mode indicator */}
        {focusMode && (
          <div className="bg-krushr-primary text-white text-sm py-1 px-4 flex items-center justify-center">
            <Focus className="w-4 h-4 mr-2" />
            Focus Mode Active - Press ⌘⇧F to exit
          </div>
        )}
        
        {/* Consolidated Workspace Header */}
        <WorkspaceHeaderConsolidated
          workspaceId={activeWorkspace.id}
          panels={panels}
          onNavigate={handleNavigate}
          onCreatePanel={handleCreatePanel}
          onAction={handleAction}
        />
        
        {/* Drag-and-Drop Panel Workspace */}
        <PanelWorkspace 
          workspaceId={activeWorkspace.id} 
          className="flex-1"
        />
        
        {/* Global Task Creation Modal */}
        {showTaskCreationModal && firstKanban && (
          <CompactTaskModal
            open={showTaskCreationModal}
            onClose={closeTaskCreationModal}
            workspaceId={activeWorkspace.id}
            kanbanId={taskCreationKanbanId || firstKanban.id}
            kanbanColumnId={taskCreationColumnId || firstKanbanData?.columns?.[0]?.id}
            onSuccess={() => {
              closeTaskCreationModal()
              // Refetch tasks across all Kanban boards
              utils.task.list.invalidate()
              utils.kanban.get.invalidate()
            }}
          />
        )}
        
        {/* Layout Manager Modal */}
        {showLayoutManager && (
          <LayoutManagerModal
            open={showLayoutManager}
            onClose={closeLayoutManager}
            workspaceId={activeWorkspace.id}
            panels={panels}
            initialMode={layoutManagerMode}
          />
        )}
        
        {/* Task View Modal for Command Palette */}
        {selectedTaskId && (
          <TaskDetail
            taskId={selectedTaskId}
            open={isTaskViewModalOpen}
            onClose={() => {
              setIsTaskViewModalOpen(false)
              setSelectedTaskId(null)
            }}
            onUpdate={() => {
              // Refetch tasks to show updated data
              utils.task.list.invalidate()
              utils.kanban.get.invalidate()
              utils.search.tasks.invalidate()
            }}
          />
        )}
      </div>
    </Layout>
  )
}